import React, { useState, useEffect } from "react";
import "./TeamDashboard.css";
import { apiGet } from "../../services/api";
import { Team } from "../../types";
import { useAuth } from "../../contexts/AuthContext";

interface TeamMember {
  userId: string;
  sessionCount: number;
  avgScore: number | null;
  lastActive: string | null;
  hasCoachingPlan: boolean;
}

interface TeamCoachingData {
  teamId: string;
  members: TeamMember[];
  totalSessions: number;
  averageScore: number | null;
}

export function TeamDashboard() {
  const { role } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const [teamData, setTeamData] = useState<TeamCoachingData | null>(null);
  const [users, setUsers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadTeams(); }, []);

  useEffect(() => {
    if (selectedTeam) loadTeamData(selectedTeam);
  }, [selectedTeam]);

  const loadTeams = async () => {
    try {
      const [teamsResult, usersResult] = await Promise.all([
        apiGet<{ items: Team[] }>("/api/teams"),
        apiGet<{ items: Array<{ userId: string; name: string }> }>("/api/users"),
      ]);
      setTeams(teamsResult.items);
      const userMap: Record<string, string> = {};
      usersResult.items.forEach((u) => { userMap[u.userId] = u.name; });
      setUsers(userMap);
      if (teamsResult.items.length > 0) {
        setSelectedTeam(teamsResult.items[0].teamId);
      }
    } catch (err) {
      console.error("Failed to load teams:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamData = async (teamId: string) => {
    try {
      const result = await apiGet<TeamCoachingData>(`/api/coaching/team/${teamId}`);
      setTeamData(result);
    } catch (err) {
      console.error("Failed to load team data:", err);
    }
  };

  if (loading) return <div className="protrain-admin-loading">Loading team data...</div>;

  return (
    <div className="protrain-team-page">
      <div className="protrain-admin-header">
        <h2 className="protrain-page-title">Team Dashboard</h2>
        {teams.length > 1 && (
          <select className="protrain-team-select" value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)}>
            {teams.map((t) => <option key={t.teamId} value={t.teamId}>{t.name}</option>)}
          </select>
        )}
      </div>

      {teamData && (
        <>
          <div className="protrain-team-stats">
            <div className="protrain-team-stat-card">
              <div className="protrain-team-stat-value">{teamData.members.length}</div>
              <div className="protrain-team-stat-label">Team Members</div>
            </div>
            <div className="protrain-team-stat-card">
              <div className="protrain-team-stat-value">{teamData.totalSessions}</div>
              <div className="protrain-team-stat-label">Total Sessions</div>
            </div>
            <div className="protrain-team-stat-card">
              <div className="protrain-team-stat-value">{teamData.averageScore ?? "—"}</div>
              <div className="protrain-team-stat-label">Avg Score</div>
            </div>
          </div>

          <table className="protrain-team-table">
            <thead>
              <tr><th>Rep</th><th>Sessions</th><th>Avg Score</th><th>Last Active</th><th>Coaching Plan</th></tr>
            </thead>
            <tbody>
              {teamData.members.map((member) => (
                <tr key={member.userId}>
                  <td className="protrain-team-member-name">{users[member.userId] || member.userId}</td>
                  <td>{member.sessionCount}</td>
                  <td>
                    {member.avgScore !== null ? (
                      <span className="protrain-team-score">{member.avgScore}/100</span>
                    ) : "—"}
                  </td>
                  <td className="protrain-team-date">
                    {member.lastActive ? new Date(member.lastActive).toLocaleDateString() : "Never"}
                  </td>
                  <td>
                    <span className={`protrain-team-plan-badge ${member.hasCoachingPlan ? "protrain-team-plan-badge--yes" : ""}`}>
                      {member.hasCoachingPlan ? "✓ Yes" : "✗ No"}
                    </span>
                  </td>
                </tr>
              ))}
              {teamData.members.length === 0 && (
                <tr><td colSpan={5} className="protrain-admin-empty">No team members with sessions yet.</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {!teamData && teams.length === 0 && (
        <div className="protrain-admin-empty">No teams configured. Ask an admin to create teams.</div>
      )}
    </div>
  );
}
